<template>
  <div>
    <div
      v-if="!loadError"
      class="wrapper mt-5">

      <h1 class="heading-bigger">{{ $t('advisersFinder.theFinanceExpert') }}</h1>
      <div class="text-grey-light text-big mt-2 mb-4 line-height-small"></div>
      <validation-observer
        ref="advisers-filters-form"
        tag="form"
        class="search-form">
        {{ $t('advisersFinder.findFinanceExpert') }}
        <f-button
          v-if="filtersBtnVisible"
          type="button"
          theme="dark"
          class="block full-width mb-3"
          @click.prevent="toggleFilters">
          <template v-if="filtersOpen">
            {{ $t('advisersFinder.collapseFilters') }}
          </template>
          <template v-else>
            {{ $t('advisersFinder.expandFilters') }}
          </template>
        </f-button>


        <transition name="slide">
          <div v-if="!filtersBtnVisible || filtersOpen">
            <div class="search-form-top flex">
              <div class="search-form-inputs flex full-width">
                <validation-provider
                  name="search-input"
                  class="full-width mr-1"
                  v-slot="{ errors }">
                  <f-input-wrapper
                    prependIcon="search"
                    class="search search-field">
                    <f-input
                      :placeholder="$t('ui.search')"
                      v-model="filters.search"/>
                  </f-input-wrapper>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>

                <validation-provider
                  name="city"
                  class="mr-1 full-width"
                  v-slot="{ errors }">
                  <f-input-wrapper
                    class="adviser-multiple-input"
                    :multipleFields="true"
                    :state="errors.length ? 'invalid' : null">
                    <f-input
                      type="text"
                      v-model="filters.city"
                      class="city"
                      :placeholder="$t('user.city')"
                      :state="errors.length ? 'invalid' : null"/>
                    <f-input-wrapper
                      append="km"
                      class="radius">
                      <f-input
                        type="number"
                        v-model="filters.radius"
                        :placeholder="$t('user.distance')"/>
                    </f-input-wrapper>
                  </f-input-wrapper>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="search-form-buttons flex">
                <f-button
                  theme="primary"
                  class="mb-1-ms"
                  big
                  @click.prevent="validateFiltersForm">
                  {{ $t('ui.search') }}
                </f-button>
                <f-button
                  theme="warning"
                  class="ml-1"
                  @click.prevent="clearFilters">
                  {{ $t('advisersFinder.resetFilters') }}
                </f-button>
              </div>
            </div>
            <div class="search-form-bottom mt-1">
              <div class="flex flex-align-items-center full-width flex-wrap">
                <div class="form-group mb-0 mr-3">
                  <f-checkbox
                    v-model="filters.onlyOnline">
                    {{ $t('advisersFinder.showOnlyOnlineExperts') }}
                  </f-checkbox>
                </div>

                <div class="flex flex-align-items-center mr-auto flex-wrap">
                  <div class="form-group mb-0 my-2">
                    <f-checkbox
                      v-model="filters.atUserDistance">
                      {{ $t('advisersFinder.inDistanceFromMe') }}
                    </f-checkbox>
                  </div>

                  <validation-provider
                    :rules="filters.atUserDistance ? 'required|min_value:1' : ''"
                    name="city"
                    class="ml-3 my-2"
                    v-slot="{ errors }">
                    <f-input-wrapper
                      append="km"
                      :state="errors.length ? 'invalid' : null">
                      <f-input
                        class="user-distance"
                        type="number"
                        v-model="filters.userRadius"
                        append="km"
                        :disabled="!filters.atUserDistance"
                        :state="errors.length ? 'invalid' : null"/>
                    </f-input-wrapper>
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>

                <div class="flex flex-align-items-center">
                  <label for="order" class="mb-0">{{ $t('advisersFinder.sort') }}:</label>
                  <v-select
                    class="minimal ml-1"
                    name="order"
                    :options="orderOptions"
                    v-model="order"
                    :reduce="(item) => { return { column: item.value, direction: item.direction } }"
                    :placeholder="$t('user.selectOrder')"
                    :clearable="false"
                    :searchable="false"
                    label="label">
                  </v-select>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </validation-observer>

      <f-alert
        v-if="locationError"
        visible
        class="my-3"
        theme="warning">
        <span>
          {{ $t('advisersFinder.errorLocation') }}
        </span>
      </f-alert>

      <div class="adivisers-list mt-3">
        <f-adviser
          v-for="(adviser, index) in advisers"
          :key="index"
          :adviser="adviser">
        </f-adviser>
        <div v-if="loaded && advisers.length === 0"
          class="f-list-no-results align-middle text-center text-bold pt-4 mt-1-md mt-3-sm">
          {{ $t('advisersFinder.noItemForShow') }}&hellip;
        </div>
      </div>

      <pagination
        :meta-data="paginationMetaData"
        :elementsCounterLabel="'advisors'" />
    </div>

    <div
      v-if="!loaded && !loadError"
      class="loading-overlay">
      <div class="loading-spinner-wrapper">
        <div class="loading-spinner pb-3 pt-3">
          <span class="loading-spinner-content">
            {{ $t('ui.loadingDataInProgress') }}&hellip;
          </span>
        </div>
      </div>
    </div>

    <div class="wrapper mt-3">
      <f-alert
        v-if="loadError"
        theme="warning"
        visible
        class="my-4">
        <span>{{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}</span>
      </f-alert>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import debounce from 'lodash.debounce';
import HasFilters from '@/mixins/HasFilters.vue';
import Pagination from '@/components/core/Pagination.vue';
import HasPagination from '@/mixins/HasPagination.vue';
import ListUtils from '@/utils/ListUtils.js';
import FormUtils from '@/utils/FormUtils.js';
import FAdviser from '@/components/layout/FAdviser.vue';

export default {
  name: 'advisers-finder',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination,
    FAdviser
  },
  data() {
    return {
      advisers: [],
      loaded: false,
      loadError: false,
      locationError: false,
      filters: {
        search: '',
        onlyOnline: false,
        city: '',
        radius: 5,
        atUserDistance: false,
        userLatitude: '',
        userLongitude: '',
        userRadius: 5
      },
      orderOptions: [
        {
          label: this.$t('advisersFinder.fromTheHighestRated'),
          value: 'opinions_avg_note',
          direction: 'DESC'
        },
        {
          label: this.$t('advisersFinder.fromTheMostFrequentlyRated'),
          value: 'opinions_count',
          direction: 'DESC'
        },
        {
          label: this.$t('advisersFinder.onSurName'),
          value: 'surname',
          direction: 'ASC'
        }
      ],
      order: {
        column: 'opinions_avg_note',
        direction: 'DESC'
      },
      filtersOpen: false,
      filtersBtnVisible: false
    };
  },
  mounted () {
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    async loadFilteredData() {
      this.loaded = false;

      let endpointParams = {
        order: this.order,
      }

      try {
        endpointParams.where = await this.prepareSearchDataToSend();

        ListUtils.loadItemsData(this, {
          method: 'get',
          endpoint: '/api/customer/advisers/search?page=' + this.currentPage,
          listField: 'advisers',
          errorKey: 'loadError',
          pagination: true,
          params: endpointParams
        });
      } catch (error) {
        console.log('error', error);
      }
    },
    async prepareSearchDataToSend () {
      let searchDataToSend = {};

      if (this.filters.search) {
        searchDataToSend.search = this.filters.search;
      }

      if (this.filters.onlyOnline) {
        searchDataToSend.only_online = +this.filters.onlyOnline;
      }

      if (this.filters.city) {
        searchDataToSend.city = this.filters.city;
      }

      if (this.filters.radius) {
        searchDataToSend.radius = this.filters.radius;
      }

      if (!this.filters.atUserDistance) {
        return searchDataToSend;
      }

      try {
        await this.getUserCoordinates();
      } catch (error) {
        console.log('error', error);
      } finally {
        searchDataToSend.at_user_distance = +this.filters.atUserDistance;
        searchDataToSend.user_latitude = this.filters.userLatitude;
        searchDataToSend.user_longitude = this.filters.userLongitude;
        searchDataToSend.user_radius = this.filters.userRadius;
      }

      return searchDataToSend;
    },
    validateFiltersForm () {
      FormUtils.validate(this.$refs['advisers-filters-form'], this.debouncedLoadFilteredData);
    },
    clearFilters () {
      this.filters = {
        search: '',
        onlyOnline: false,
        city: '',
        radius: 5,
        atUserDistance: false,
        userLatitude: '',
        userLongitude: '',
        userRadius: 5
      }

      this.order = {
        column: 'opinions_avg_note',
        direction: 'DESC'
      }

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    async getUserCoordinates () {
      try {
        await this.getCoordinates().then(position => {
          Vue.set(this, 'locationError', false);
          Vue.set(this.filters, 'userLatitude', position.coords.latitude);
          Vue.set(this.filters, 'userLongitude', position.coords.longitude);
        });
      } catch (error) {
        Vue.set(this, 'locationError', true);
        console.log('error', error);
      }
    },
    getCoordinates() {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });
    },
    toggleFilters () {
      Vue.set(this, 'filtersOpen', !this.filtersOpen);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';

.search-form .f-checkbox:after {
  top: 4px;
}

.user-distance {
  max-width: 150px;
}

@media screen and (max-width: $media-tablet-m) {
  .user-distance {
    max-width: 110px;
  }
}
</style>
